import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from './shadcn/button';
import { toast } from 'sonner';
import { createStripePromise } from '../utils/stripe-utils';

// Make sure to add your Stripe publishable key to your .env file
const stripePromise = createStripePromise(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ plan, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    // Create PaymentIntent as soon as the page loads
    const createPaymentIntent = async () => {
      try {
        const response = await fetch('/api/billing/create-payment-intent', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            planId: plan.id,
            planName: plan.name,
            amount: plan.price * 100 // Convert to cents
          }),
        });
        
        const data = await response.json();
        
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          toast.error(data.message || 'Failed to initialize payment');
        }
      } catch (error) {
        toast.error('Failed to initialize payment. Please try again.');
        console.error('Error creating payment intent:', error);
      }
    };

    if (plan) {
      createPaymentIntent();
    }
  }, [plan]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      toast.error('Payment system is still loading. Please try again.');
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      // Confirm the payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: 'Customer Name', // In a real app, you'd get this from the user's profile
          },
        },
      });

      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        toast.success('Payment successful! Your subscription has been activated.');
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      toast.error('An unexpected error occurred during payment processing.');
      console.error('Payment error:', err);
    }

    setLoading(false);
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Payment Details</h3>
        <div className="border rounded-lg p-4">
          <CardElement options={cardElementOptions} />
        </div>
      </div>
      
      <div className="flex justify-between items-center pt-4">
        <div>
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold">${plan?.price}/month</p>
        </div>
        <div className="flex gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={!stripe || loading}
          >
            {loading ? 'Processing...' : `Pay $${plan?.price}`}
          </Button>
        </div>
      </div>
    </form>
  );
};

const StripeCheckout = ({ plan, onSuccess, onCancel }) => {
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeError, setStripeError] = useState(false);

  useEffect(() => {
    // Check if Stripe is properly configured
    if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      console.warn('Stripe publishable key is not set in environment variables');
    }

    // Check if stripePromise resolves successfully
    stripePromise.then((stripe) => {
      if (stripe) {
        setStripeReady(true);
      } else {
        setStripeError(true);
        console.error('Failed to initialize Stripe. Check your publishable key.');
      }
    }).catch((error) => {
      setStripeError(true);
      console.error('Error initializing Stripe:', error);
    });
  }, []);

  if (stripeError) {
    return (
      <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
        <h3 className="text-lg font-medium text-red-800 mb-2">Payment System Error</h3>
        <p className="text-red-700 mb-4">
          There was an error initializing the payment system. Please contact support.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      {process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY && stripeReady ? (
        <Elements stripe={stripePromise}>
          <CheckoutForm plan={plan} onSuccess={onSuccess} onCancel={onCancel} />
        </Elements>
      ) : !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? (
        <div className="text-center p-6 bg-yellow-50 rounded-lg">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Stripe Configuration Required</h3>
          <p className="text-yellow-700 mb-4">
            To enable payments, please add your Stripe publishable key to your environment variables.
          </p>
          <p className="text-sm text-yellow-600">
            REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_...
          </p>
        </div>
      ) : (
        <div className="text-center p-6 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-medium text-blue-800 mb-2">Loading Payment System...</h3>
          <p className="text-blue-700">Please wait while we initialize the payment system.</p>
        </div>
      )}
    </div>
  );
};

export default StripeCheckout;